import { inject } from '@angular/core';
import { Router, CanActivateFn, UrlTree } from '@angular/router';
import { map, take, catchError, switchMap } from 'rxjs/operators';
import { SubdomainResolver } from '../resolvers/subdomain.resolver';
import { of, Observable } from 'rxjs';
import { CustomAuthService } from '../services/custom-auth.service';

/**
 * Auth guard for protecting routes that require authentication
 *
 * For subdomains: Redirect to ROOT DOMAIN login if not authenticated
 * For root domain: Redirect to /login if not authenticated
 */
export const authWithPortalRedirectGuard: CanActivateFn = (route, state): Observable<boolean | UrlTree> => {
    const authService = inject(CustomAuthService);
    const router = inject(Router);
    const subdomainResolver = inject(SubdomainResolver);
    const subdomain = subdomainResolver.resolve();

    console.log('[AuthGuard] Checking route:', state.url, 'subdomain:', subdomain);

    // Skip guard for login, auth, and organization-not-found pages to prevent redirect loops
    if (state.url.includes('/login') || state.url.includes('/auth/') || state.url.includes('/organization-not-found')) {
        console.log('[AuthGuard] Skipping guard for auth-related page');
        return of(true);
    }

    return authService.isLoading$.pipe(
        take(1),
        switchMap(loading => {
            if (loading) {
                // Still loading, wait for auth to complete
                return authService.isAuthenticated$.pipe(take(1));
            }
            // Check auth state immediately
            return authService.isAuthenticated$.pipe(take(1));
        }),
        map(isAuthenticated => {
            console.log('[AuthGuard] isAuthenticated:', isAuthenticated);
            if (isAuthenticated) {
                console.log('[AuthGuard] User is authenticated, allowing access');
                return true;
            }

            // Not authenticated - redirect to login
            console.log('[AuthGuard] Not authenticated, redirecting to login');

            // If on a subdomain, redirect to root domain login
            if (subdomain) {
                const protocol = window.location.protocol;
                const port = window.location.port;
                const portSuffix = (port && port !== '80' && port !== '443') ? `:${port}` : '';

                let rootDomainUrl: string;
                if (window.location.hostname.includes('localhost')) {
                    rootDomainUrl = `${protocol}//localhost${portSuffix}/login`;
                } else {
                    const rootDomain = window.location.hostname.split('.').slice(-2).join('.');
                    rootDomainUrl = `${protocol}//${rootDomain}${portSuffix}/login`;
                }

                console.log('[AuthGuard] Redirecting to root domain login:', rootDomainUrl);
                window.location.href = rootDomainUrl;
                // Return false to prevent navigation while redirect happens
                return false;
            }

            // On root domain, use router navigation
            const urlTree = router.createUrlTree(['/login'], {
                queryParams: { returnUrl: state.url }
            });
            console.log('[AuthGuard] UrlTree created:', urlTree.toString());
            return urlTree;
        }),
        catchError(error => {
            console.error('Auth guard error:', error);
            // Redirect to login page on error
            return of(router.createUrlTree(['/login']));
        })
    );
};
